import { intersection } from "lodash";
import { makeAutoObservable } from "mobx";
import { v4 as uuid } from "uuid";

import { graphql } from "@/gql";
import {
  AllergiesKind,
  FoodItemFragment,
  IngredientItemFragment,
} from "@/gql/graphql";
import { getPortionWeightGrams } from "@/utils/food";

type Macros = {
  foodWeight: number;
  calories: number;
  carbohydrates: number;
  proteins: number;
  fats: number;
};

const defaultValues: Macros = {
  foodWeight: 1,
  calories: 1,
  carbohydrates: 1,
  proteins: 1,
  fats: 1,
};

export class Ingredient {
  id = uuid();
  quantity = 0;
  ingredient: FoodItemFragment | IngredientItemFragment["food"] | undefined =
    undefined;
  portionSize: IngredientItemFragment["foodPortion"] | undefined = undefined;
  portionSizeId = "";
  isIngredientAllergenModalShown = false;

  constructor(
    id?: string,
    quantity?: number,
    ingredient?: FoodItemFragment | IngredientItemFragment["food"] | undefined,
    portionSize?: IngredientItemFragment["foodPortion"] | undefined
  ) {
    makeAutoObservable(this);
    this.id = id || "";
    this.quantity = quantity || 0;
    this.ingredient = ingredient;
    this.portionSize = portionSize;
  }

  // Getters
  get hasIngredient(): boolean {
    return this.ingredient != undefined;
  }

  get hasAmount(): boolean {
    return this.quantity != 0;
  }

  get hasPortionSize(): boolean {
    return this.portionSize != undefined;
  }

  get macros(): Macros {
    const food = this.ingredient;
    const portion = this.ingredient?.portions.find(
      (portion) => portion.id === this.portionSize?.id
    );
    if (!food || !portion) return defaultValues;

    const foodWeight = getPortionWeightGrams(portion, this.quantity);
    const weightPercent = foodWeight / food.epWeightGrams;
    const calories = food.energyKcal * weightPercent;
    const carbohydrates = (food.carbohydrateGrams ?? 0) * weightPercent;
    const proteins = (food.proteinGrams ?? 0) * weightPercent;
    const fats = (food.fatGrams ?? 0) * weightPercent;

    return {
      foodWeight,
      calories,
      carbohydrates,
      proteins,
      fats,
    };
  }

  setQuantity = (value: number): void => {
    this.quantity = value;
  };

  setIngredient = (ingredient: FoodItemFragment): void => {
    this.ingredient = ingredient;
  };

  setPortionSize = (portionSizeId: string): void => {
    this.portionSizeId = portionSizeId;
    this.portionSize = this.ingredient?.portions.find(
      (portion) => portion.id === portionSizeId
    );
  };

  hasAllergies = (clientAllergies?: AllergiesKind[]): boolean => {
    return (
      intersection(this.ingredient?.allergies, clientAllergies).length !== 0
    );
  };

  reset = (): void => {
    this.quantity = 0;
    this.ingredient = undefined;
    this.portionSize = undefined;
    this.portionSizeId = "";
  };

  showAllergenModal = (isShown: boolean): void => {
    this.isIngredientAllergenModalShown = isShown;
  };
}

// gql
export const ingredientItemFragment = graphql(`
  fragment IngredientItem on Ingredient {
    id
    amount
    food {
      id
      name
      epWeightGrams
      energyKcal
      carbohydrateGrams
      proteinGrams
      fatGrams
      source
      sourceId
      category
      portions {
        id
        amount
        unitWithModifier
        weightGrams
      }
      allergies
    }
    foodPortion {
      id
      amount
      unitWithModifier
      weightGrams
    }
  }
`);
